import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@shared/auth';
import { useCurrentOrg } from '@shared/data/use-current-org';
import { capture } from '@shared/analytics/capture';
import type { InvitePayload, Member, Scope } from './schemas';
import {
  acceptInvite,
  createShareableLink,
  forceSignout,
  getInviteByToken,
  inviteMember,
  listInvites,
  listJoinRequests,
  listMembers,
  removeMember,
  resendInvite,
  resolveJoinRequest,
  revokeInvite,
  transferOwnership,
  updateMemberRole,
  updateMemberScope,
  updateMemberStatus,
  updateMemberTeam,
  type CreateShareableLinkPayload,
  type InviteMemberPayload,
} from './client';

/** Backend member-mutation routes key on `userId`; fixtures carry none, so
 *  fall back to the membership id (`m.id`) when `userId` is absent. */
function memberKey(m: Member): string {
  return m.userId ?? m.id;
}

export const teamKeys = {
  members: (orgId: string) => ['team-members', { orgId }] as const,
  invitations: (orgId: string) => ['team-invitations', { orgId }] as const,
  joinRequests: (orgId: string) => ['team-join-requests', { orgId }] as const,
  /** Token-based invite lookup (public, unauthenticated). */
  inviteToken: (token: string) => ['invite-token', { token }] as const,
};

/**
 * Fetch invite details by token (public endpoint, no auth required).
 *
 * On 404 (not found / already used) or 400 (expired) the query errors —
 * consumers inspect `error` to derive the token state.
 */
export function useInviteByToken(token: string | null) {
  return useQuery({
    queryKey: teamKeys.inviteToken(token ?? ''),
    queryFn: () => getInviteByToken(token!),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });
}

export function useMembers(orgId: string) {
  return useQuery({
    queryKey: teamKeys.members(orgId),
    queryFn: () => listMembers(orgId),
    enabled: !!orgId,
  });
}

/* ---------- member management mutations ---------- */

export function useUpdateMemberScope(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, scope }: { userId: string; scope: Scope | null }) =>
      updateMemberScope(orgId, userId, scope),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.members(orgId) }),
  });
}

export function useUpdateMemberStatus(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: 'active' | 'suspended' }) =>
      updateMemberStatus(orgId, userId, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.members(orgId) }),
  });
}

export function useUpdateMemberRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      updateMemberRole(orgId, userId, role),
    onSuccess: (_data, { role }) => {
      capture('member_role_updated', { role });
      qc.invalidateQueries({ queryKey: teamKeys.members(orgId) });
    },
  });
}

export function useUpdateMemberTeam(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, teamId }: { userId: string; teamId: string | null }) =>
      updateMemberTeam(orgId, userId, teamId),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.members(orgId) }),
  });
}

export function useRemoveMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => removeMember(orgId, userId),
    onSuccess: () => {
      capture('member_removed');
      qc.invalidateQueries({ queryKey: teamKeys.members(orgId) });
    },
  });
}

export function useForceSignout(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => forceSignout(orgId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.members(orgId) }),
  });
}

/* ---------- bulk member actions ---------- */

/**
 * A bulk operation over the currently-selected members:
 *   - `role`    → PATCH /members/:userId/role
 *   - `team`    → PATCH /members/:userId/team   (cm#309)
 *   - `suspend`    → PATCH /members/:userId/status  (status: 'suspended')
 *   - `reactivate` → PATCH /members/:userId/status  (status: 'active')
 *   - `remove`     → DELETE /members/:userId
 */
export type BulkMemberOp =
  | { kind: 'role'; role: string }
  | { kind: 'team'; teamId: string | null }
  | { kind: 'suspend' }
  | { kind: 'reactivate' }
  | { kind: 'remove' };

/** Apply a bulk op to a member list in-memory — the optimistic projection. */
function projectBulk(
  members: ReadonlyArray<Member>,
  selectedIds: ReadonlySet<string>,
  op: BulkMemberOp
): Member[] {
  if (op.kind === 'remove') {
    return members.filter((m) => !selectedIds.has(m.id));
  }
  return members.map((m) => {
    if (!selectedIds.has(m.id)) return m;
    if (op.kind === 'role') return { ...m, role: op.role };
    if (op.kind === 'team') return { ...m, teamId: op.teamId };
    if (op.kind === 'reactivate') return { ...m, status: 'active' as const };
    return { ...m, status: 'suspended' as const };
  });
}

/** Fire the per-member request for a single member under a bulk op. */
function runBulkRequest(orgId: string, m: Member, op: BulkMemberOp): Promise<void> {
  const key = memberKey(m);
  if (op.kind === 'role') return updateMemberRole(orgId, key, op.role);
  if (op.kind === 'team') return updateMemberTeam(orgId, key, op.teamId);
  if (op.kind === 'remove') return removeMember(orgId, key);
  if (op.kind === 'reactivate') return updateMemberStatus(orgId, key, 'active');
  return updateMemberStatus(orgId, key, 'suspended');
}

/**
 * Optimistic bulk-action hook. Snapshots the members cache, applies the op
 * optimistically, fans the op out to one request per selected member, rolls
 * the cache back if any request fails, and re-syncs from the server on settle.
 * Surfaces the count of failures so the caller can flash an accurate toast.
 */
export function useBulkMemberAction(orgId: string) {
  const qc = useQueryClient();
  const key = teamKeys.members(orgId);

  return useMutation<
    { total: number; failed: number },
    Error,
    { members: ReadonlyArray<Member>; op: BulkMemberOp },
    { previous: Member[] | undefined }
  >({
    onMutate: async ({ members, op }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Member[]>(key);
      if (previous) {
        const selectedIds = new Set(members.map((m) => m.id));
        qc.setQueryData<Member[]>(key, projectBulk(previous, selectedIds, op));
      }
      return { previous };
    },
    mutationFn: async ({ members, op }) => {
      const results = await Promise.allSettled(members.map((m) => runBulkRequest(orgId, m, op)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0 && failed === results.length) {
        // Total failure — reject so onError rolls the optimistic cache back.
        // Surface the backend reason (e.g. the rank-guard 403 message) rather
        // than a generic string; ApiError.message is the flattened body message.
        const reason = results.find((r) => r.status === 'rejected')?.reason;
        const message =
          reason instanceof Error ? reason.message : `All ${failed} member actions failed`;
        throw new Error(message);
      }
      return { total: results.length, failed };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useTransferOwnership(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ targetUserId }: { targetUserId: string }) =>
      transferOwnership(orgId, targetUserId),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.members(orgId) }),
  });
}

/* ---------- invite mutation ---------- */

export function useInviteMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: InviteMemberPayload) => inviteMember(orgId, payload),
    onSuccess: () => {
      capture('member_invited');
      void qc.invalidateQueries({ queryKey: teamKeys.members(orgId) });
      void qc.invalidateQueries({ queryKey: teamKeys.invitations(orgId) });
    },
  });
}

/* ---------- invite queries + mutations ---------- */

export function useInvites(orgId: string) {
  return useQuery({
    queryKey: teamKeys.invitations(orgId),
    queryFn: () => listInvites(orgId),
    enabled: !!orgId,
  });
}

export function useResendInvite(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ inviteId }: { inviteId: string }) => resendInvite(orgId, inviteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.invitations(orgId) }),
  });
}

export function useRevokeInvite(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ inviteId }: { inviteId: string }) => revokeInvite(orgId, inviteId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.invitations(orgId) });
      void qc.invalidateQueries({ queryKey: teamKeys.members(orgId) });
    },
  });
}

/* ---------- accept invitation ---------- */

export function useAcceptInvite() {
  const { refresh } = useAuth();
  const { setOrg } = useCurrentOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => acceptInvite(token),
    // After joining, the new org must show up in the switcher and become the
    // landing org. session.orgs comes from /auth/userInfo, so re-hydrate it
    // first (refresh), THEN switch to the accepted org — setOrg validates the id
    // against session.orgs, so the order matters. This awaited onSuccess runs
    // before the caller's own onSuccess (its navigate('/dashboard')), so the
    // dashboard lands on the right org. The accepted org id comes from the
    // invite still cached under teamKeys.inviteToken(token).
    onSuccess: async (_res, token) => {
      const invite = qc.getQueryData<InvitePayload>(teamKeys.inviteToken(token));
      await refresh();
      if (invite?.orgId) setOrg(invite.orgId);
    },
  });
}

/* ---------- shareable invite link ---------- */

export function useCreateShareableLink(orgId: string) {
  return useMutation({
    mutationFn: (payload: CreateShareableLinkPayload) => createShareableLink(orgId, payload),
  });
}

/* ---------- join request queries + mutations ---------- */

export function useJoinRequests(orgId: string) {
  return useQuery({
    queryKey: teamKeys.joinRequests(orgId),
    queryFn: () => listJoinRequests(orgId),
    enabled: !!orgId,
  });
}

export function useResolveJoinRequest(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      approved,
      role,
    }: {
      requestId: string;
      approved: boolean;
      role?: string;
    }) => resolveJoinRequest(orgId, requestId, approved, role),
    onSuccess: (_data, { approved }) => {
      capture('join_request_resolved', { approved });
      void qc.invalidateQueries({ queryKey: teamKeys.joinRequests(orgId) });
      void qc.invalidateQueries({ queryKey: teamKeys.members(orgId) });
    },
  });
}
